import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Platform,
  LoadingController,
  AlertController,
  ModalController
} from '@ionic/angular';
import {
  CoreRouterActionGoBack,
  CoreActionSaveDeeplinkFormData,
  selectorCoreLogin,
  selectorCoreInstanceInfo,
  CoreUtilsService,
  CoreExternalAppService,
  ENVIRONMENT,
  Environment
} from '@bepatient/core';
import {
  selectorMyFormForm,
  MyFormActionSaveCurrentPage
} from '@bepatient/my-form-store';
import { Store, select } from '@ngrx/store';
import { Subscription, firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { WebviewModalComponent } from '../webview-modal/webview-modal.component';
// import { environment } from '../../../../../../apps/smart-app/src/environments/environment';

@Component({
  selector: 'lib-bridge-webview',
  templateUrl: './bridge-webview.component.html',
  styleUrl: './bridge-webview.component.css',
  standalone: false
})
export class BridgeWebviewComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private platform = inject(Platform);
  private loadingController = inject(LoadingController);
  private alertController = inject(AlertController);
  private modalController = inject(ModalController);
  private store = inject<Store<any>>(Store);
  private coreUtilsService = inject(CoreUtilsService);
  private coreExternalAppService = inject(CoreExternalAppService);
  private env = inject<Environment>(ENVIRONMENT);

  private subscription: Subscription = new Subscription();
  isLoading = false;
  isModalReady = false;
  hasOpenedModal = false;
  webviewUrl = '';
  webviewTitle = '';
  uniqueKey = '';
  completionStatus = '';

  async ngOnInit() {
    // Component initialized

    // Debug: Check store state
    this.debugStoreState();

    // Get URL parameters first
    this.subscription.add(
      this.route.queryParams.subscribe(async params => {
        // Received query params

        this.webviewUrl = params['url'];
        this.webviewTitle = params['title'] || '';
        this.uniqueKey = params['uniqueKey'] || 'unknown';
        const target = params['target'] || 'pvt'; // Get target from query params, default to 'pvt'

        // Validate target is allowed
        if (!this.coreExternalAppService.isTargetAllowed(target)) {
          console.error(`Target '${target}' is not allowed in webview`);
          return;
        }

        // Decode the URL for processing
        const decodedUrl = decodeURIComponent(this.webviewUrl || '');

        // Append user ID to assessment URLs if needed
        if (this.webviewUrl) {
          this.webviewUrl = await this.appendUserIdToUrl(decodedUrl, target);
        }

        // Note: Deeplink callbacks are now handled by the global listener in CoreUtilsService
        // This ensures callbacks work even when the app is in the background

        if (this.webviewUrl && !this.hasOpenedModal) {
          this.hasOpenedModal = true;
          await this.openWebview();
        }
        // Don't show error if no URL - component might be loaded but not active
      })
    );

    // Listen for browser close events
    this.subscription.add(
      this.platform.backButton.subscribeWithPriority(10, async () => {
        await this.closeWebview();
      })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  private async openWebview() {
    this.isLoading = true;
    this.isModalReady = false;

    try {
      // Create the webview modal
      const formColor = this.getCurrentFormColor();

      const modal = await this.modalController.create({
        component: WebviewModalComponent,
        componentProps: {
          url: this.webviewUrl,
          title: this.webviewTitle,
          color: formColor,
          completionStatus: this.completionStatus
        },
        cssClass: 'webview-modal-sheet',
        backdropDismiss: true,
        showBackdrop: true,
        // Enable swipe to dismiss
        canDismiss: true,
        handle: true
      });

      // Present the modal
      await modal.present();

      // Mark modal as ready
      this.isModalReady = true;
      this.isLoading = false;

      // Handle modal dismissal
      await modal.onDidDismiss();

      // Always mark as completed when modal is dismissed (regardless of how)
      // This ensures the button gets disabled when user closes the modal
      this.handleWebviewCompletionWithoutCallback();

      this.isLoading = false;
      this.isModalReady = false;
      this.hasOpenedModal = false; // Reset flag when modal is dismissed

      // Navigate back to timeline after modal closes
      this.store.dispatch(new CoreRouterActionGoBack());
    } catch (error) {
      console.error('Error opening webview modal:', error);
      this.isLoading = false;
      this.isModalReady = false;
      this.hasOpenedModal = false; // Reset flag on error
      await this.showError('Failed to load the assessment');
    }
  }

  private async closeWebview() {
    // Close any open modals
    try {
      await this.modalController.dismiss();
    } catch (error) {
      console.error('Error closing webview modal:', error);
    }

    // Reset flags
    this.hasOpenedModal = false;
    this.isModalReady = false;
    this.isLoading = false;

    // Navigate back to timeline
    this.store.dispatch(new CoreRouterActionGoBack());
  }

  private async showError(message: string) {
    const alert = await this.alertController.create({
      header: 'Error',
      message: message,
      buttons: [
        {
          text: 'OK',
          handler: () => {
            this.store.dispatch(new CoreRouterActionGoBack());
          }
        }
      ]
    });

    await alert.present();
  }

  // Handle successful assessment completion
  private async handleExternalAppCompletion() {
    try {
      // Update completion status
      this.completionStatus = 'completed';

      // Close the webview modal
      await this.modalController.dismiss({
        status: 'completed',
        completedAt: new Date().toISOString()
      });

      // Store completion status in localStorage using robust structure
      this.storeDeeplinkFormCompletion(this.uniqueKey, {
        completed: true,
        completedAt: new Date().toISOString(),
        uniqueKey: this.uniqueKey
      });

      // Reset flags
      this.hasOpenedModal = false;
      this.isModalReady = false;
      this.isLoading = false;

      // Show success message
      const alert = await this.alertController.create({
        header: 'Assessment Completed',
        message: 'Assessment has been completed successfully.',
        buttons: [
          {
            text: 'OK',
            handler: () => {
              // Navigate back to form/timeline
              this.store.dispatch(new CoreRouterActionGoBack());
            }
          }
        ]
      });

      await alert.present();
    } catch (error) {
      console.error('Error handling assessment completion:', error);
    }
  }

  // Handle webview completion without deeplink callback
  private handleWebviewCompletionWithoutCallback(): void {
    // Store completion status in localStorage using robust structure
    this.storeDeeplinkFormCompletion(this.uniqueKey, {
      completed: true,
      completedAt: new Date().toISOString(),
      method: 'manual-close',
      uniqueKey: this.uniqueKey
    });
  }

  // Store deeplink form completion data in localStorage and dispatch action
  private storeDeeplinkFormCompletion(
    uniqueKey: string,
    completionData: any
  ): void {
    try {
      const existingData = this.getDeeplinkFormCompletionData();
      existingData[uniqueKey] = completionData;
      localStorage.setItem(
        'deeplink-form-completions',
        JSON.stringify(existingData)
      );

      // Dispatch action to trigger button state updates
      this.store.dispatch(
        new CoreActionSaveDeeplinkFormData({
          uniqueKey: uniqueKey,
          completionData: completionData
        })
      );

      // Auto-submit form for this occurrence
      this.autoSubmitFormForOccurrence(uniqueKey);
    } catch (error) {
      console.error('Error storing deeplink form completion:', error);
    }
  }

  // Auto-submit form when external app assessment is completed
  private autoSubmitFormForOccurrence(_uniqueKey: string): void {
    try {
      // Dispatch SAVE_CURRENT_PAGE to follow the normal form submission flow
      // This will trigger the same API call and response handling as manual submission
      this.store.dispatch(new MyFormActionSaveCurrentPage());
    } catch (error) {
      console.error('Error auto-submitting form:', error);
    }
  }

  // Get deeplink form completion data from localStorage
  private getDeeplinkFormCompletionData(): any {
    try {
      const data = localStorage.getItem('deeplink-form-completions');
      return data ? JSON.parse(data) : {};
    } catch (error) {
      console.error('Error getting deeplink form completion data:', error);
      return {};
    }
  }

  // Handle assessment error or failure
  private async handleExternalAppError(urlParams: URLSearchParams) {
    try {
      const status = this.completionStatus || 'error';

      // Close the webview modal
      await this.modalController.dismiss({
        status: status,
        errorAt: new Date().toISOString()
      });

      // Store failure status in localStorage
      this.storeDeeplinkFormCompletion(this.uniqueKey, {
        completed: false,
        status: status,
        failedAt: new Date().toISOString(),
        uniqueKey: this.uniqueKey
      });

      // Reset flags
      this.hasOpenedModal = false;
      this.isModalReady = false;
      this.isLoading = false;

      // Get error details from URL params
      const errorMessage =
        urlParams.get('message') || 'An error occurred during the assessment';

      // Show error message
      const alert = await this.alertController.create({
        header:
          status === 'failed' ? 'Assessment Failed' : 'External Link Error',
        message: `Assessment ${status}: ${errorMessage}`,
        buttons: [
          {
            text: 'OK',
            handler: () => {
              // Navigate back to form/timeline
              this.store.dispatch(new CoreRouterActionGoBack());
            }
          }
        ]
      });

      await alert.present();
    } catch (error) {
      console.error('Error handling assessment error/failure:', error);
    }
  }

  // Handle unknown assessment status
  private async handleExternalAppUnknownStatus(
    status: string,
    _urlParams: URLSearchParams
  ) {
    try {
      // Close the webview modal
      await this.modalController.dismiss({
        status: status,
        processedAt: new Date().toISOString()
      });

      // Store status in localStorage
      this.storeDeeplinkFormCompletion(this.uniqueKey, {
        completed: false,
        status: status,
        processedAt: new Date().toISOString(),
        uniqueKey: this.uniqueKey
      });

      // Reset flags
      this.hasOpenedModal = false;
      this.isModalReady = false;
      this.isLoading = false;

      // Show unknown status message
      const alert = await this.alertController.create({
        header: 'External Link Status',
        message: `Status: ${status}`,
        buttons: [
          {
            text: 'OK',
            handler: () => {
              // Navigate back to form/timeline
              this.store.dispatch(new CoreRouterActionGoBack());
            }
          }
        ]
      });

      await alert.present();
    } catch (error) {
      console.error('Error handling unknown status:', error);
    }
  }

  // Method to handle manual close (if needed for UI)
  async onCloseWebview() {
    await this.closeWebview();
  }

  /**
   * Debug method to check store state
   */
  private debugStoreState(): void {
    this.store.pipe(select(selectorCoreLogin), take(1)).subscribe(loginData => {
      if (loginData) {
      } else {
      }
    });
  }

  /**
   * Append dynamic user ID to assessment URL if not already present
   * @param url The original URL
   * @param target The target type (e.g., 'pvt', 'gta')
   * @returns URL with dynamic user ID appended
   */
  private async appendUserIdToUrl(
    url: string,
    target = 'pvt'
  ): Promise<string> {
    try {
      // Get all required data from store
      const [loginData, formData] = await Promise.all([
        firstValueFrom(this.store.pipe(select(selectorCoreLogin))),
        firstValueFrom(this.store.pipe(select(selectorMyFormForm)))
      ]);

      if (!loginData) {
        console.warn('No login data available in store');
        return url;
      }

      // Use centralized utility for dynamic URL building
      const isMobileDevice =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );

      return await this.coreUtilsService.buildDynamicExternalAppUrl(
        url,
        loginData,
        formData,
        this.env,
        isMobileDevice,
        target // Dynamic target parameter
      );
    } catch (error) {
      console.error('Error appending user ID to URL:', error);
      return url; // Return original URL if error occurs
    }
  }

  // Get current form color from store
  private getCurrentFormColor(): string {
    let color = '';
    this.store
      .select(selectorMyFormForm)
      .pipe(take(1))
      .subscribe((form: any) => {
        if (form && form.color) {
          color = form.color;
        }
      });

    if (!color) {
      this.store
        .select(selectorCoreInstanceInfo)
        .pipe(take(1))
        .subscribe((instanceInfo: any) => {
          if (instanceInfo && instanceInfo.appColor) {
            color = instanceInfo.appColor;
          }
        });
    }

    return color || '#3880ff'; // Default blue color
  }
}
